import "./DealStructureList.css";

import { Dispatch, SetStateAction } from "react";

import { EditGreyIcon, RoundedPlusSignIcon, TrashDark } from "../../../../../../../assets";
import { Button, Column, Row } from "../../../../../../../widget";
import { DealStructureListItem as DealStructureListItemType } from "../../useDealStructure";

interface DealStructureListProps {
  isEditing: boolean;
  isSaving: boolean;
  selectedInvestmentKey: string | undefined;
  setSelectedInvestmentKey: Dispatch<SetStateAction<string | undefined>>;
  dealStructureItems: DealStructureListItemType[];
  onRemoveInvestment: (key: string) => void;
  onAddInvestment: () => void;
  startEditing: () => void;
  cancelEditing: () => void;
  saveDealStructure: () => Promise<void>;
  canEdit: boolean;
}

export const DealStructureList = ({
  isEditing,
  isSaving,
  selectedInvestmentKey,
  setSelectedInvestmentKey,
  dealStructureItems,
  onRemoveInvestment,
  onAddInvestment,
  startEditing,
  cancelEditing,
  saveDealStructure,
  canEdit,
}: DealStructureListProps): JSX.Element => {
  return (
    <div className="DealStructureCard DealStructureListCard">
      <div className="DealStructureCardHeader">
        <h2>Deal structure</h2>
        <div className="DealStructureHeaderActions">
          {!isEditing && canEdit && (
            <button
              type="button"
              className="DealStructureListEditButton"
              onClick={startEditing}
              aria-label="Edit deal structure"
            >
              <EditGreyIcon />
            </button>
          )}
        </div>
      </div>
      {isEditing && (
        <Row>
          <Column>
            <Button
              text="Add investment"
              icon={<RoundedPlusSignIcon />}
              iconPosition="left"
              variant="reversedPrimary"
              hasNoPadding
              className="DealStructureAddButton"
              onClick={onAddInvestment}
            />
          </Column>
        </Row>
      )}
      {dealStructureItems.length === 0 ? (
        <p className="body2 DealStructureEmpty">No deal structure available.</p>
      ) : (
        <div className="DealStructureList">
          {dealStructureItems.map((item) => (
            <div
              key={item.key}
              role="button"
              tabIndex={0}
              className={`DealStructureListItem ${
                item.key === selectedInvestmentKey ? "DealStructureListItemSelected" : ""
              }`}
              onClick={() => setSelectedInvestmentKey(item.key)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  setSelectedInvestmentKey(item.key);
                }
              }}
            >
              <div className="DealStructureListItemContent">
                <div className="DealStructureListItemText">
                  <p className="body2 DealStructureListItemTitle">{item.title}</p>
                  <p className="body3 DealStructureListItemSubtitle">{item.subtitle}</p>
                </div>
                {isEditing && (
                  <button
                    type="button"
                    className="DealStructureListTrashButton"
                    aria-label="Remove investment"
                    onClick={(event) => {
                      event.stopPropagation();
                      onRemoveInvestment(item.key);
                    }}
                  >
                    <TrashDark className="DealStructureListTrashIcon" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {isEditing && (
        <div className="DealStructureListActions">
          <Button
            text="Cancel"
            variant="tertiary"
            size="smallLarge"
            corners="rounded"
            onClick={cancelEditing}
            fullWidth
          />
          <Button
            text="Save"
            variant="primary"
            size="smallLarge"
            corners="rounded"
            isLoading={isSaving}
            onClick={saveDealStructure}
            fullWidth
          />
        </div>
      )}
    </div>
  );
};
